import React, { useState } from 'react';

type Props = {
  thumbnail?: string;
  title: string;
  className?: string;
  videoUrl: string;
};

export const DemoVideo: React.FC<Props> = ({ 
  thumbnail, 
  title, 
  className, 
  videoUrl 
}) => {
  const [showVideo, setShowVideo] = useState(false);

  const handleClick = () => {
    setShowVideo(true);
  };

  // detect video platform and extract necessary info
  const getVideoInfo = (url: string) => {
    if (!url) return { platform: 'unknown', embedUrl: '' };

    // check for YouTube
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const youtubeMatch = url.match(youtubeRegExp);
    if (youtubeMatch && youtubeMatch[2].length === 11) {
      return {
        platform: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[2]}?autoplay=1`
      };
    }

    // check for Google Drive
    const driveRegExp = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const driveMatch = url.match(driveRegExp);
    if (driveMatch) {
      return {
        platform: 'drive',
        embedUrl: `https://drive.google.com/file/d/${driveMatch[1]}/preview`
      };
    }

    // check if it's already a Google Drive embed URL
    const driveEmbedRegExp = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/preview/;
    if (driveEmbedRegExp.test(url)) {
      return {
        platform: 'drive',
        embedUrl: url
      };
    }

    return { platform: 'unknown', embedUrl: '' };
  };

  const videoInfo = getVideoInfo(videoUrl);

  // render different UI based on video platform
  const renderVideoContent = () => {
    if (videoInfo.platform === "drive") {
      // for Google Drive, show video directly since no autoplay
      return (
        <div className="w-full">
          <iframe
            src={videoInfo.embedUrl}
            title={title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-[16.4rem] md:h-[24rem] rounded-t-[0.4rem] border-[0.2rem] border-gray-200"
          />
        </div>
      );
    }

    // for YouTube, use thumbnail with play button
    if (videoInfo.platform === "youtube") {
      return (
        <div className="cursor-pointer" onClick={handleClick}>
          {showVideo ? (
            <iframe
              src={videoInfo.embedUrl}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-[16.4rem] md:h-[24rem] rounded-t-[0.4rem] border-[0.2rem] border-gray-200"
            />
          ) : (
            <div className="relative">
              <img
                src={thumbnail}
                alt=""
                className="w-full h-[16.4rem] md:h-[24rem] rounded-t-[0.4rem] border-[0.2rem] border-gray-200 object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-black bg-opacity-50 rounded-full p-3 flex items-center justify-center hover:bg-opacity-70 transition-all duration-200">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M9 18L18 12L9 6V18Z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // fallback for unknown platforms
    return (
      <div className="w-full h-[16.4rem] md:h-[24rem] rounded-t-[0.4rem] border-[0.2rem] border-gray-200 bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Unsupported video format</p>
      </div>
    );
  };

  return (
    <div className="w-full lg:max-w-[34rem] mx-auto">
      <div
        className={`flex flex-col space-y-1 rounded-[0.4rem] w-full pb-2 ${
          className || ""
        }`}
      >
        {renderVideoContent()}
        <div className="px-2">
          <p className="text-[#3C4257] font-medium py-[0.5rem] text-sm md:text-base">
            {title}
          </p>
        </div>
      </div>
    </div>
  );
};
